import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useAuthContext } from '../contexts/AuthContext';
import { businessService } from '../services/businessService';
import { usePageTitle } from '../hooks/usePageTitle';

const ListingSubmissionSuccess: React.FC = () => {
  const [searchParams] = useSearchParams();
  const { currentUser } = useAuthContext();
  const listingId = searchParams.get('listing');
  const [listing, setListing] = useState<any>(null);

  usePageTitle('Listing Submitted Successfully | BizBuyMax.com');

  useEffect(() => {
    const loadListing = async () => {
      if (!listingId || !currentUser) {
        return;
      }

      try {
        const listingData = await businessService.getBusiness(listingId);
        if (listingData && listingData.owner.userId === currentUser.uid) {
          setListing(listingData);
        }
      } catch (error) {
        console.error('Error loading listing:', error);
      }
    };

    loadListing();
  }, [listingId, currentUser]);

  // Check if listing uses a REALTOR-inclusive package
  const isRealtorPackage = listing?.package?.id === '6months';

  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-600 text-lg mb-4">⚠️ Please sign in to view this page</div>
          <Link to="/signin" className="bg-primary-600 text-white px-4 py-2 rounded hover:bg-primary-700">
            Sign In
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Listing Submitted Successfully | BizBuyMax.com</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50">
        <div className="max-w-3xl mx-auto px-4 py-16">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200/50 p-8 md:p-12">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
                <CheckCircleIcon className="w-12 h-12 text-green-600" />
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Your Listing is Now Active!
              </h1>
              <p className="text-lg text-gray-700">
                Your business listing has been successfully submitted and is now live on BizBuyMax.com.
              </p>
            </div>

            {/* REALTOR Package Notice */}
            {isRealtorPackage && (
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8 rounded-r-lg">
                <h2 className="text-lg font-semibold text-blue-900 mb-3">
                  REALTOR.ca Listing Submission
                </h2>
                <p className="text-blue-800 leading-relaxed">
                  Since you selected the REALTOR package, a partnering real estate agent will submit your listing to REALTOR.ca and notify us of the MLS number.
                </p>
                <div className="mt-4 p-4 bg-blue-100 rounded-lg">
                  <p className="text-sm text-blue-900 font-medium">
                    ⚠️ Important Reminder:
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    The agent is <strong>NOT</strong> representing you and is <strong>NOT</strong> receiving a commission. They are simply facilitating the listing submission to REALTOR.ca.
                  </p>
                </div>
              </div>
            )}

            {/* Listing Details */}
            {listing && (
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Listing Details</h3>
                <div className="space-y-2">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Title:</span>
                    <span className="font-medium text-gray-900">{listing.basic.title}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Location:</span>
                    <span className="font-medium text-gray-900">
                      {listing.location.city}, {listing.location.province}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Status:</span>
                    <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                      Active
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/account/dashboard"
                className="px-8 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white rounded-xl font-semibold transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 text-center"
              >
                Go to My Account
              </Link>
              {listing && (
                <Link
                  to={listing.basic.seoUrl ? `/business/${listing.basic.seoUrl}` : `/business/${listing.id}`}
                  className="px-8 py-3 bg-white border-2 border-primary-500 text-primary-600 hover:bg-primary-50 rounded-xl font-semibold transition-all duration-300 shadow-md hover:shadow-lg text-center"
                >
                  View Listing
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ListingSubmissionSuccess;
